import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Timer as TimerIcon,
  Sms as SmsIcon
} from '@mui/icons-material';
import { SchedulerLog } from '@/types/smsScheduler';
import { formatErrorForDisplay } from '@/utils/errorUtils';

interface SchedulerLogsTableProps {
  logs: SchedulerLog[];
  loading?: boolean;
  error?: string | null;
}

const SchedulerLogsTable: React.FC<SchedulerLogsTableProps> = ({
  logs,
  loading = false,
  error = null
}) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <SuccessIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'started':
        return <PendingIcon color="warning" />;
      default:
        return <PendingIcon />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'started':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDateTime = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return 'N/A';
    
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds.toFixed(1)}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  };

  const getSuccessRate = (log: SchedulerLog): number => {
    if (log.policies_processed === 0) return 0;
    return Math.round((log.successful_count / log.policies_processed) * 100);
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {formatErrorForDisplay(error)}
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading scheduler logs...
        </Typography>
      </Box>
    );
  }

  if (logs.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <SmsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No Scheduler Logs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Scheduler execution logs will appear here
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Schedule Name</TableCell>
            <TableCell align="center">Execution Time</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Policies</TableCell>
            <TableCell align="center">Success Rate</TableCell>
            <TableCell align="center">Duration</TableCell>
            <TableCell align="center">Batch ID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {logs.map((log) => (
            <TableRow key={log.id} hover>
              <TableCell>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {log.schedule_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Config ID: {log.scheduler_config_id}
                  </Typography>
                </Box>
              </TableCell>
              
              <TableCell align="center">
                <Typography variant="body2">
                  {formatDateTime(log.execution_time)}
                </Typography>
              </TableCell>
              
              <TableCell align="center">
                <Chip
                  icon={getStatusIcon(log.execution_status)}
                  label={log.execution_status.charAt(0).toUpperCase() + log.execution_status.slice(1)}
                  color={getStatusColor(log.execution_status)}
                  size="small"
                />
              </TableCell>
              
              <TableCell align="center">
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {log.policies_processed} / {log.policies_found}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    processed / found
                  </Typography>
                </Box>
              </TableCell>
              
              <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    {getSuccessRate(log)}%
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Chip
                      label={log.successful_count}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                    <Chip
                      label={log.failed_count}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </TableCell>
              
              <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TimerIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    {formatDuration(log.execution_duration_seconds)}
                  </Typography>
                </Box>
              </TableCell>
              
              <TableCell align="center">
                {log.batch_id ? (
                  <Tooltip title={log.batch_id}>
                    <Typography variant="caption" color="primary" sx={{ cursor: 'pointer' }}>
                      {log.batch_id.substring(0, 8)}...
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    N/A
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SchedulerLogsTable;
